// The one way an Extra team's externally-scheduled opponent renders, on every
// surface that shows it (design spec §7): the coach portal's own schedule, the
// admin Schedule/Adjust grid and dialogs, and the field-duty preview.
//
// ⚠️ LEAF MODULE, same convention as teamLabel.ts — must never import from
// './index'. That includes the JSON case table: the cases live here as a typed
// const, and `externalMatchLabelCases.json` is the copy the Python twin reads.
// `externalMatchLabel.test.ts` fails if the two stop agreeing.

import { TEAM_LABEL_SEPARATOR } from './teamLabel'

/** ⚠️ OWNER WORDING, 2026-09-14: `Area/Section/Extra`. Both halves are load
 *  bearing — the region says "Area or Section" to people (owner, 2026-09-02),
 *  but "Extra" is what is printed on a card and said on a sideline, so the
 *  marker carries both rather than trading one confusion for another.
 *  Twinned with `src/export/brand.py`; change one and the parity test fails,
 *  which is the point. */
export const EXTRA_MATCH_MARKER = 'Area/Section/Extra'

/** PURE. "<opponent_label> · Area/Section/Extra" — the marker that tells an operator,
 *  a coach, or a field marshal that this game is not a Region 13 match.
 *
 *  Blank opponent -> the bare marker, so a fixture entered without a readable
 *  opponent still reads as an Area/Section/Extra match rather than as a dangling separator.
 *
 *  ⚠️ The input is TRIMMED. `external_fixtures.opponent_label` is free text and
 *  nothing trims it on the way in (`createExternalFixture` rejects only the
 *  empty string, so "   " and "  Conejo Valley  " both store as typed), and the
 *  Python twin in `src/export/brand.py` has always trimmed. Without the trim
 *  here the same fixture read "  Conejo Valley   · Area/Section/Extra" on the SPA and
 *  "Conejo Valley · Area/Section/Extra" in the PDF.
 *
 *  ⚠️ The parameter is `string`, NOT `string | null | undefined`, and that is
 *  load-bearing. Every production caller reaches this function behind a
 *  truthiness check on a nullable column, so the wide signature bought nothing
 *  and cost the compiler's ability to stop the ONE call this function must
 *  never serve: a null label returning the bare marker labels an ordinary
 *  Region 13 game an Area/Section/Extra one. A caller that genuinely may have nothing says
 *  so by name — see `externalOpponentLabelOrMarker`. */
export function externalOpponentLabel(opponentLabel: string): string {
  const text = opponentLabel.trim()
  return text ? `${text}${TEAM_LABEL_SEPARATOR}${EXTRA_MATCH_MARKER}` : EXTRA_MATCH_MARKER
}

/** The nullable entry point: "I am reading a column that may be NULL, and if it
 *  is I still want the marker." Same output as `externalOpponentLabel` for
 *  every non-null input, and the bare marker for null/undefined — the contract
 *  `brand.external_opponent_label` has always had on the Python side, which the
 *  shared case table below and the parity test pin.
 *
 *  Reach for it only where the absent case is genuinely a fixture with no
 *  readable opponent. Where a null means "this is not an Extra game at all",
 *  the truthiness check belongs at the call site, not in here. */
export function externalOpponentLabelOrMarker(
  opponentLabel: string | null | undefined,
): string {
  return externalOpponentLabel(opponentLabel ?? '')
}

export interface ExternalMatchLabelCase {
  opponentLabel: string | null
  expected: string
}

/** The shared case table. `externalMatchLabel.test.ts` walks it, and so does the
 *  Python twin's parity test (`web/container/tests/test_external_match_label_parity.py`)
 *  via `web/packages/shared-types/src/externalMatchLabelCases.json`.
 *
 *  Two implementations of one display rule is the failure this repo keeps
 *  recording, and this marker is the worst place for it: it is the ONLY thing on
 *  a public schedule PDF, a `.ics` SUMMARY or a coach's move email that
 *  distinguishes an Extra team's externally-scheduled fixture from an ordinary
 *  Region 13 match, so a drift between the twins fails nowhere — it just makes
 *  half the surfaces stop saying what the game is.
 *
 *  ⚠️ This table replaced a parity test that RESTATED the composition rule in
 *  Python (`f"{opponent}{sep}{marker}"`). Restating a rule cannot detect a
 *  change to it: reversing this module to `${MARKER}${SEP}${opponent}` left all
 *  three parity tests green. Expected labels are literals here on purpose —
 *  never compose one from the constants, or the table restates the rule too. */
export const EXTERNAL_MATCH_LABEL_CASES = [
  { opponentLabel: 'Conejo Valley', expected: 'Conejo Valley · Area/Section/Extra' },
  { opponentLabel: 'AYSO Region 8', expected: 'AYSO Region 8 · Area/Section/Extra' },
  // The bullet, never " - ": a hyphenated opponent must stay one segment, the
  // same ruling `teamLabel`'s `Rao - Asteroids` case pins.
  { opponentLabel: 'Camarillo - Gold', expected: 'Camarillo - Gold · Area/Section/Extra' },
  // Trimming. Both twins must agree, and for a while they did not.
  { opponentLabel: '  Conejo Valley  ', expected: 'Conejo Valley · Area/Section/Extra' },
  // Blank in every shape a free-text column can hand over -> the bare marker,
  // never a label that opens with a dangling separator.
  { opponentLabel: '', expected: 'Area/Section/Extra' },
  { opponentLabel: '   ', expected: 'Area/Section/Extra' },
  { opponentLabel: null, expected: 'Area/Section/Extra' },
] as const satisfies readonly ExternalMatchLabelCase[]

/** The name to render for the VISITING side of a matchup: the Area/Section
 *  club an operator typed on the Requests page when the game is that team's
 *  externally-scheduled home fixture, otherwise the stored team designation.
 *
 *  ⚠️ EXISTS BECAUSE A HOME FIXTURE HAS `games.team2_id IS NULL` BY DESIGN.
 *  The opponent is another region's club and has no `teams` row, so every
 *  query that reads `t2.external_id` and stops renders the game as "vs TBD" —
 *  which is what the referee product did on every one of its surfaces until
 *  2026-09-15, including the `.ics` feed and the assignment emails. The name
 *  lives in `external_fixtures.opponent_label`, reached from
 *  `games.external_fixture_id`.
 *
 *  ⚠️ RETURNS THE BARE NAME, NEVER THE MARKER. `externalOpponentLabel` above
 *  bundles the "· Area/Section/Extra" marker INTO the string, which is right
 *  for a caller that renders the opponent and nothing else (the scheduler's
 *  `opponentRef`, the coach portal's referee view). The referee surfaces append
 *  that marker separately from `is_extra` (`slotSummaryLabel`), so handing them
 *  the bundled form prints it twice. Pick one owner for the marker per surface;
 *  this function deliberately is not it.
 *
 *  Null when neither side is known, which is what `matchupText` turns into
 *  "TBD" — an unplaced away fixture still reads as unresolved, correctly. */
export function visitingSideName(
  visitingDesignation: string | null | undefined,
  opponentLabel: string | null | undefined,
): string | null {
  const external = opponentLabel?.trim()
  return external ? external : (visitingDesignation ?? null)
}
